import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import DemoBadge from "./DemoBadge";

/**
 * PulseMap — stylized SVG city grid with pulsing zones and flowing traffic lines.
 * Refresh interval: 3s per spec.
 */
export const PulseMap = ({ refreshMs = 3000 }) => {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/engine/map-zones").then(r => alive && setData(r.data));
    load();
    const id = setInterval(() => { setTick(t => t + 1); load(); }, refreshMs);
    return () => { alive = false; clearInterval(id); };
  }, [refreshMs]);

  const zones = data?.zones || [];
  const flows = data?.flows || [];
  const pos = (n) => zones.find(z => z.name === n);

  const riskColor = (risk, alpha = 1) =>
    risk === "high" ? `rgba(229,62,62,${alpha})` :
    risk === "moderate" ? `rgba(236,201,75,${alpha})` :
    `rgba(45,156,219,${alpha})`;

  return (
    <div className="glass p-0 overflow-hidden" data-testid="pulse-map">
      <div className="flex items-center justify-between p-5 border-b border-gold-dim/20">
        <div>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-electric">
            <span className="pulse-dot" /> System Map · Asset Status
          </div>
          <h3 className="font-display text-xl mt-1">Live Infrastructure Pulse</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Refresh</div>
          <div className="text-xs text-cream-dim">every {Math.round(refreshMs/1000)}s · update #{tick}</div>
          <DemoBadge />
        </div>
      </div>

      <div className="relative" style={{ aspectRatio: "16 / 9", background:
        "radial-gradient(ellipse at center, rgba(45,156,219,0.12), transparent 70%), linear-gradient(180deg, #081530, #0D1F3C)" }}>
        <svg viewBox="0 0 100 56" className="absolute inset-0 w-full h-full">
          {/* City grid */}
          <g stroke="rgba(45,156,219,0.12)" strokeWidth="0.15">
            {Array.from({ length: 16 }).map((_, i) => (
              <line key={`v${i}`} x1={(i + 1) * 6} y1="0" x2={(i + 1) * 6} y2="56" />
            ))}
            {Array.from({ length: 10 }).map((_, i) => (
              <line key={`h${i}`} x1="0" y1={(i + 1) * 5} x2="100" y2={(i + 1) * 5} />
            ))}
          </g>

          {/* Flow lines (traffic) */}
          {flows.map((f, i) => {
            const a = pos(f.from), b = pos(f.to);
            if (!a || !b) return null;
            const ax = a.x, ay = a.y * 0.56;
            const bx = b.x, by = b.y * 0.56;
            const color = f.load > 0.75 ? "#E53E3E" : f.load > 0.55 ? "#ECC94B" : "#2D9CDB";
            return (
              <g key={i}>
                <line x1={ax} y1={ay} x2={bx} y2={by}
                  stroke={color} strokeOpacity="0.35" strokeWidth="0.5" />
                <circle r="0.7" fill={color}>
                  <animateMotion
                    dur={`${2 + (1 - f.load) * 3}s`} repeatCount="indefinite"
                    path={`M${ax},${ay} L${bx},${by}`}
                  />
                </circle>
                <circle r="0.5" fill={color} opacity="0.7">
                  <animateMotion
                    dur={`${2.6 + (1 - f.load) * 3}s`} begin="0.6s" repeatCount="indefinite"
                    path={`M${ax},${ay} L${bx},${by}`}
                  />
                </circle>
              </g>
            );
          })}

          {/* Zones */}
          {zones.map((z, i) => (
            <g key={z.name}>
              <circle cx={z.x} cy={z.y * 0.56} r={1 + z.intensity * 2.5}
                fill={riskColor(z.risk, 0.25)} stroke={riskColor(z.risk, 0.9)} strokeWidth="0.25">
                <animate attributeName="r"
                  values={`${1 + z.intensity * 2.5};${1 + z.intensity * 3.8};${1 + z.intensity * 2.5}`}
                  dur={`${1.6 + i * 0.18}s`} repeatCount="indefinite" />
              </circle>
              <text x={z.x} y={z.y * 0.56 - 2.2} fontSize="1.6" textAnchor="middle"
                fill="#F4EFE6" opacity="0.8" style={{ letterSpacing: "0.05em" }}>
                {z.name.toUpperCase()}
              </text>
            </g>
          ))}
        </svg>

        {/* legend */}
        <div className="absolute top-4 right-4 glass-accent px-3 py-2 text-[10px] space-y-1">
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[color:var(--auditiq-electric)]"/> Low</div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[color:var(--auditiq-warning)]"/> Moderate</div>
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[color:var(--auditiq-critical)]"/> High</div>
        </div>
      </div>
    </div>
  );
};

export default PulseMap;
